import { useLazyQuery } from "@apollo/client";
import { Box, Grid, Stack, Typography, styled } from "@mui/material";
import React, { useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CANCEL_AUTOPAY_SUCCESS_TITLE,
  DEFAULT_AUTOPAY_PAPERLESS_DISCOUNT_MESSAGES,
  DEFAULT_NOTE_AUTOPAY,
  DEFAULT_PRIMARY_TEXT_AUTOPAY,
  DEFAULT_SECONDARY_TEXT_AUTOPAY,
  NOT_FOUND,
} from "../../../../Constants/constants";
import { AUTOPAY_PAPERLESS_DETAILS } from "../../../../Pages/Billing/query";
import { setErrorFlag } from "../../../../Redux/Slices/AutoPayPaperlessSlice";
import { setShowNextinAutoPay } from "../../../../Redux/Slices/OrderUpgradeSlice";
import { convertToPositive, getBannerMessageByKey } from "../../../../Services/helper";
import {
  BlankScreenStack,
  PageGrid,
  ProfileTypography,
  SpanTypography,
  StickyBox,
  StyledPaper,
} from "../../../../Styles/GlobalStyles";
import AutopayIcon from "../../../../assets/icons/AutopayIcon.svg";
import Button from "../../../Button/Button";
import { AccountCard } from "../../../shared/AccountCard/AccountCard";
import { Spinner } from "../../LoadingSpinner/Spinner";
import { NeedHelpCard } from "../../NeedHelpCard/NeedHelpCard";

interface SaveAutoPayProps {
  title: string;
  description?: string;
  primaryText?: any;
  secondaryText?: string;
  note?: string;
  orderUpgrade?: any;
}

export const SaveAutoPay: React.FC<SaveAutoPayProps> = ({
  title,
  description,
  primaryText = DEFAULT_PRIMARY_TEXT_AUTOPAY,
  secondaryText = DEFAULT_SECONDARY_TEXT_AUTOPAY,
  note = DEFAULT_NOTE_AUTOPAY,
  orderUpgrade,
}) => {
  const { state, pathname } = useLocation();
  const ACCOUNT = state?.ACCOUNT;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const handlePayment = () => {
    navigate({ pathname: "/billing/makeapayment" });
  };
  const { scrollPosition } = useSelector((state: any) => state.pageInfo);
  const { accountNumber } = useSelector((state: any) => state.customerInfoDetails);
  const { autopayAmount, autopaySurchargeDetails, isSMB, showAutopayDiscountMessage } = useSelector(
    (state: any) => state.customerPlanDetails
  );
  const sideCoreBannerMessages = useSelector(
    (state: any) => state?.sideCoreContent?.bannerMessages
  );
  const sidecoreDiscountMessages = getBannerMessageByKey(
    "autopay-paperless-discount-messages",
    sideCoreBannerMessages,
    false
  );
  const autopayPaperlessDiscountMessages = Object.fromEntries(
    Object.keys(DEFAULT_AUTOPAY_PAPERLESS_DISCOUNT_MESSAGES).map((key) => [
      key,
      sidecoreDiscountMessages?.[key] || DEFAULT_AUTOPAY_PAPERLESS_DISCOUNT_MESSAGES[key],
    ])
  );
  const [
    getAutopayPaperlessDetails,
    { loading: autopayPaperlessLoading, data: autopayPaperlessData },
  ] = useLazyQuery(AUTOPAY_PAPERLESS_DETAILS, {
    variables: {
      accountNumber: accountNumber,
    },
    onError: (error: any) => {
      if (error.networkError.result.errors[0]?.message?.toLowerCase()?.includes(NOT_FOUND)) {
        dispatch(setErrorFlag(true));
      } else {
        dispatch(setErrorFlag(false));
      }
    },
    fetchPolicy: "network-only",
  });

  const autopayDetails =
    autopayPaperlessData?.autoPayAndPaperlessDetails?.autoPayDetails?.autoPayInfo?.[0];

  const handleNavigate = (path: string) => () => {
    navigate(path);
  };

  useLayoutEffect(() => {
    window?.scrollTo?.(0, 0);
    getAutopayPaperlessDetails();
    if (orderUpgrade) {
      dispatch(setShowNextinAutoPay(false));
    }
  }, []);

  return (
    <Box mt={orderUpgrade ? 0 : 6}>
      <PageGrid container>
        <Grid item xs={12} lg={orderUpgrade ? 12 : 8.2}>
          <BlankScreenStack textAlign={"center"}>
            {autopayPaperlessLoading ? (
              <Box py={36}>
                <Spinner />
              </Box>
            ) : (
              <Stack mt={orderUpgrade ? "" : "20px"} alignItems={"center"}>
                {title !== CANCEL_AUTOPAY_SUCCESS_TITLE && (
                  <SaveAutopayImage src={AutopayIcon} alt={"autopay-icon"} />
                )}
                <SaveAutopayStack gap={"1rem"}>
                  <Typography fontWeight={600} fontSize={{ xs: "16px", md: "20px" }}>
                    {title}
                  </Typography>
                  {autopayDetails && (
                    <SpanTypography data-testid="account-details">
                      Your&nbsp;
                      {autopayDetails?.paymentMethodName}
                      &nbsp; will be charged&nbsp;
                      {convertToPositive(autopayDetails?.scheduleDayBefore ?? "")} day(s) before
                      your due date.
                    </SpanTypography>
                  )}
                  {description && (
                    <SpanTypography data-testid="account-details">{description}</SpanTypography>
                  )}
                  {orderUpgrade && (
                    <ProfileTypography>
                      Note: Your autopay discount will be applied according to your payment method:
                      $10/month for ACH or debit, or $5/month for credit card payments.
                    </ProfileTypography>
                  )}
                </SaveAutopayStack>

                {!orderUpgrade && (
                  <>
                    <AutopayPaper>
                      <Stack
                        direction={"row"}
                        spacing={1.7}
                        alignItems={"center"}
                        justifyContent={"center"}>
                        <SaveAutopayStack>
                          {title === CANCEL_AUTOPAY_SUCCESS_TITLE && (
                            <SaveAutopayTypography>Alert!</SaveAutopayTypography>
                          )}
                          <Typography sx={{ fontSize: { xs: "12px", md: "14px" } }}>
                            {primaryText}
                          </Typography>
                          {title !== CANCEL_AUTOPAY_SUCCESS_TITLE && (
                            <SaveAutopayTypography>{secondaryText}</SaveAutopayTypography>
                          )}
                        </SaveAutopayStack>
                      </Stack>
                      <Stack direction={"row"} my={"20px"} justifyContent={"center"}>
                        <Button
                          title="Make a one time payment"
                          type="root"
                          onClick={handlePayment}
                        />
                      </Stack>
                      {title === CANCEL_AUTOPAY_SUCCESS_TITLE &&
                        autopayAmount &&
                        !isSMB &&
                        showAutopayDiscountMessage &&
                        autopayPaperlessDiscountMessages["autopay-deactivated"] && (
                          <Typography sx={{ fontSize: { xs: "12px", md: "14px" } }}>
                            {autopayPaperlessDiscountMessages["autopay-deactivated"]}
                          </Typography>
                        )}
                    </AutopayPaper>
                    <SaveAutopayStack gap={"2rem"} mb={"10px"}>
                      <Typography sx={{ fontSize: { xs: "12px", md: "14px" } }}>
                        Thanks for choosing Ziply Fiber.
                      </Typography>
                      <SaveAutopaySubTypography>{note}</SaveAutopaySubTypography>
                    </SaveAutopayStack>
                    {pathname === "/account/manageautopay" || ACCOUNT ? (
                      <>
                        <Button
                          onClick={handleNavigate("/account")}
                          type="pay"
                          title="GO TO ACCOUNT"
                        />
                        <br />
                      </>
                    ) : (
                      <>
                        <Button
                          onClick={handleNavigate("/billing")}
                          type="pay"
                          title="GO TO BILLING"
                        />
                        <br />
                      </>
                    )}
                  </>
                )}
              </Stack>
            )}
          </BlankScreenStack>
        </Grid>
        {!orderUpgrade && (
          <>
            <Grid
              item
              mt={{ xs: "2rem", lg: "0" }}
              xs={12}
              lg={3.5}
              display={{ xs: "block", lg: scrollPosition > 50 ? "block" : "none" }}>
              <AccountCard />
              <NeedHelpCard />
            </Grid>

            <StickyBox display={{ xs: "none", lg: scrollPosition > 50 ? "none" : "block" }}>
              <AccountCard />
              <NeedHelpCard />
            </StickyBox>
          </>
        )}
      </PageGrid>
    </Box>
  );
};

const SaveAutopayStack = styled(Stack)(({ theme }) => ({
  justifyContent: "center",
  alignItems: "center",
}));

export const SaveAutopayTypography = styled(Typography)(({ theme }) => ({
  fontWeight: "600",
  [theme.breakpoints.down("sm")]: { fontSize: "12px" },
}));

const AutopayPaper = styled(StyledPaper)(({ theme }) => ({
  width: "700px",
  backgroundColor: theme.palette.customcolor.saveAutoPayPaperColor,
  boxShadow: "none",
}));

const SaveAutopaySubTypography = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  color: theme.palette.customcolor.profileTypoGraphyColor,
}));

const SaveAutopayImage = styled("img")(({ theme }) => ({
  marginBottom: "20px",
  [theme.breakpoints.down("sm")]: { height: "55px" },
}));
